<template>
  <div>
    <div class="wise-promotion-banner"></div>
    <div class="payments">
      <div class="row">
        <div class="col-sm-12">
          <div id="payment-rates">
            <ValidationProvider ref="payment_rates">
              <AppToggleButtonGroup
                :value="paymentSection"
                :label="toggleButtonTooltipLabel"
                :label-tooltip="toggleButtonTooltipText"
                :tooltip-options="toggleButtonTooltipOptions"
                :items="PAYMENT_RATES"
                :selected="paymentSection"
                @input="togglePaymentSection"
              />
            </ValidationProvider>
          </div>
        </div>
      </div>

      <div
        v-if="!isBillRate && !hasCustomPayPeriodDates"
        class="row"
      >
        <div class="col-xs-12 col-md-6">
          <div
            id="pay-period"
            class="form-group"
          >
            <ValidationProvider>
              <label class="control-label">Pay period</label>
              <AppSelect
                v-model="payPeriod"
                :options="payPeriodOptions"
                :select2-options="defaultSelect2Options"
                :disabled="payPeriodIsExternal"
                @input="onPayPeriodSelect"
              />
            </ValidationProvider>
          </div>
        </div>
        <div
          v-if="canEditTimesheets"
          class="col-xs-12 col-md-6 mb-sm-0 mb-20"
        >
          <label class="control-label d-flex align-items-center">
            past timesheets
            <i
              v-tooltip
              class="hi hi-12 a-tooltip hi-info-filled mx-5"
              data-placement="right"
              data-original-title="Create and edit past timesheets"
            />
          </label>
          <div>
            <button
              class="btn btn-transparent-outline edit-timesheets-btn"
              data-testid="edit-timesheets-btn"
              @click="toggleAdjustTimesheetsDialog(true)"
            >
              Edit timesheets
            </button>
          </div>
        </div>
        <div
          v-if="!isBillRate && canApproveTimesheets"
          class="col-md-12 col-xs-12 mt-sm-10"
        >
          <div
            id="timesheets-enabled"
            class="form-group"
          >
            <ValidationProvider>
              <div class="checkbox">
                <AppOnOffToggle
                  v-model="timesheetsEnabled"
                  :disabled="timesheetToggleDisabled"
                >
                  <span>Require timesheet approval</span>
                </AppOnOffToggle>
              </div>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <div
        v-if="!hasCustomPayPeriodDates && showPeriodStartOptions"
        class="row mb-10"
      >
        <div class="col-sm-12">
          <ValidationProvider
            v-slot="{ errors }"
            name="startOn"
            mode="eager"
            rules="required"
          >
            <div
              id="start-on"
              class="d-flex align-items-baseline flex-column flex-sm-row gap-10"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="control-label mb-0">Start on:</label>
              <div class="d-flex flex-column flex-sm-row period-start-options">
                <span
                  v-for="(date, index) of startDates"
                  :key="index"
                >
                  <label
                    :for="`period-start-${index}`"
                    class="period-start-option label label-lg label-rounded a-tooltip mb-10 mr-5"
                    :class="date.value === periodStart ? 'label-primary period-start-selected' : 'label-gray-light'"
                  >
                    {{ formatPeriodDate(date.value) }}
                  </label>
                  <input
                    :id="`period-start-${index}`"
                    v-model="periodStart"
                    type="radio"
                    name="period-start"
                    :value="date.value"
                  />
                </span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <div v-if="!isBillRate && hasCustomPayPeriodDates">
        <h4 class="mt-32 mb-0">Pay periods</h4>
        <p class="mb-24 text-grey">Set how worked hours are grouped into timesheets.</p>

        <PayPeriodCustomDatesSelector
          :value="customPayPeriodSelectorValue"
          :initial-state="customPayPeriodSelectorInitialState"
          :pay-period-options="customPayPeriodOptions"
          :pay-period-select-disabled="payPeriodIsExternal"
          :organization-timezone="organizationTimeZone?.for"
          frequency-hint="How often timesheets reset."
          weekly-hint="The day each timesheet period begins."
          monthly-hint="The day each timesheet period begins."
          twice-per-month-hint="The dates each timesheet period begins."
          @input="onCustomPayPeriodSelectorInput"
        />
      </div>

      <div
        v-if="!isBillRate && hasCustomPayPeriodDates"
        class="row mt-15"
      >
        <div
          v-if="canEditTimesheets"
          class="col-xs-12 col-md-6 mb-sm-0 mb-20"
        >
          <label class="control-label">Past timesheets</label>
          <div>
            <button
              class="btn btn-transparent-outline edit-timesheets-btn"
              data-testid="edit-timesheets-btn"
              @click="toggleAdjustTimesheetsDialog(true)"
            >
              Edit timesheets
            </button>
          </div>
        </div>
        <div
          v-if="canApproveTimesheets"
          class="col-xs-12 col-md-6"
        >
          <label class="control-label">Require timesheet approval</label>
          <div class="form-group mb-0 mt-10">
            <ValidationProvider>
              <div class="checkbox">
                <AppOnOffToggle
                  v-model="timesheetsEnabled"
                  :disabled="timesheetToggleDisabled"
                >
                  <span>Yes</span>
                </AppOnOffToggle>
              </div>
            </ValidationProvider>
          </div>
        </div>
      </div>

      <div
        v-if="hasCustomPayPeriodDates && !isBillRate"
        class="pay-details-row"
      >
        <h4 class="mt-32 mb-24">Pay details</h4>
      </div>

      <div class="row">
        <template v-if="!isBillRate">
          <div class="pay-rate-wrapper col-sm-6 col-xs-12">
            <template v-if="!hasFixedRate">
              <ValidationProvider
                v-slot="{ errors }"
                name="payRate"
                mode="eager"
                :rules="`payRateRule|max_value:${RATE_LIMIT}`"
              >
                <div
                  id="pay-rate"
                  class="form-group"
                  :class="{ 'has-error': errors[0] || formErrors('pay_rate') }"
                >
                  <label class="control-label">Pay rate</label>

                  <AppInput
                    v-model="payRate"
                    :placeholder="organizationCurrency"
                    type="number"
                  />
                  <span
                    v-if="errors[0]"
                    class="help-block"
                  >
                    {{ errors[0] }}
                  </span>
                  <span
                    v-if="formErrors('pay_rate')"
                    class="help-block"
                  >
                    {{ formErrors('pay_rate') }}
                  </span>
                </div>
              </ValidationProvider>
            </template>
            <template v-else>
              <ValidationProvider
                v-slot="{ errors }"
                name="fixedAmount"
                mode="eager"
                :rules="`required|greaterThanZero|max_value:${RATE_LIMIT}`"
              >
                <div
                  id="fixed-amount"
                  class="form-group"
                  :class="{ 'has-error': errors[0] }"
                >
                  <label class="control-label">Fixed amount</label>
                  <AppInput
                    v-model="fixedAmount"
                    :placeholder="organizationCurrency"
                    type="number"
                  />
                  <span
                    v-if="errors[0]"
                    class="help-block"
                  >
                    {{ errors[0] }}
                  </span>
                </div>
              </ValidationProvider>
            </template>
            <ValidationProvider>
              <div
                id="rate-type"
                class="form-group"
              >
                <AppSelect
                  v-model="rateType"
                  :options="rateTypeOptions"
                  :select2-options="defaultSelect2Options"
                  :disabled="payPeriodIsNone || payPeriodIsExternal"
                  disabled-tooltip="Select a pay period in order to set a pay type"
                />
              </div>
            </ValidationProvider>
          </div>
          <ValidationProvider>
            <div
              v-if="showPayRateEffectiveDate"
              class="col-sm-6 col-xs-12"
            >
              <div class="label-wrapper">
                <label class="control-label">Effective date</label>
              </div>
              <AppDatePicker
                v-model="payRateEffectiveDate"
                data-testid="member-pay-rate-effective-date"
                type="daily"
                :show-quick-pick="false"
                :max-date="maxRateEffectiveDate"
                :min-date="minRateEffectiveDate.payRate"
                @input="onPayRateEffectiveDateInput"
              />
            </div>
          </ValidationProvider>
        </template>
        <div
          v-if="isBillRate"
          class="col-sm-6 col-xs-12"
        >
          <ValidationProvider
            v-slot="{ errors }"
            name="billRate"
            mode="eager"
            :rules="`greaterThanZero|max_value:${RATE_LIMIT}`"
          >
            <div
              id="bill-rate"
              class="form-group"
              :class="{ 'has-error': errors[0] }"
            >
              <label class="control-label">Bill rate</label>
              <div class="input-group">
                <AppInput v-model="billRate" />
                <span class="input-group-addon">{{ organizationCurrency }}/hr</span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div
          v-if="isBillRate"
          class="col-sm-6 col-xs-12"
        >
          <label class="control-label">Effective date</label>
          <AppDatePicker
            v-model="billRateEffectiveDate"
            data-testid="member-bill-rate-effective-date"
            type="daily"
            :show-quick-pick="false"
            :max-date="maxRateEffectiveDate"
            :min-date="minRateEffectiveDate.billRate"
            @input="onBillRateEffectiveDateInput"
          />
        </div>
      </div>

      <AppConfirm
        v-if="showEffectiveDateConfirm"
        title="Override existing rates"
        confirm-label="Continue"
        @cancel="onEffectiveDateConfirmCancel"
        @confirm="onEffectiveDateConfirmConfirm"
      >
        {{ effectiveDateConfirmMessage() }}
      </AppConfirm>

      <div
        v-if="isBillRate"
        class="row"
      >
        <div
          v-if="!showBillRateNote"
          class="col-sm-12"
        >
          <a
            href="#"
            class="control-label-btn"
            style="display: flex; align-items: center; margin-top: 10px; text-decoration: none; cursor: pointer;"
            @click.prevent="displayBillRateNote"
          >
            <i class="hi hi-blog hi-12" />
            <span class="ml-5">Add note</span>
          </a>
        </div>
      </div>
      <div
        v-else
        class="row"
      >
        <div
          v-if="!showPayRateNote"
          class="col-sm-12"
        >
          <a
            href="#"
            class="control-label-btn"
            style="display: flex; align-items: center; margin-top: 20px; text-decoration: none; cursor: pointer;"
            @click.prevent="displayPayRateNote"
          >
            <i class="hi hi-blog hi-12" />
            <span class="ml-5">Add note</span>
          </a>
        </div>
      </div>
      <ValidationProvider
        v-slot="{ errors }"
        rules="noteMax:100"
        mode="eager"
      >
        <div
          v-if="(isBillRate && showBillRateNote) || (!isBillRate && showPayRateNote)"
          class="form-group"
          :class="{ 'has-error': errors[0] }"
        >
          <label
            :for="isBillRate ? 'bill-rate-note' : 'pay-rate-note'"
            class="control-label"
            :class="{ 'text-danger': errors[0] }"
          >
            Note
          </label>
          <div
            v-if="(isBillRate && showBillRateNote) || (!isBillRate && showPayRateNote)"
            class="control-label-btn pull-right"
            @click="isBillRate ? removeBillRateNote() : removePayRateNote()"
          >
            <a>
              Remove note
            </a>
          </div>
          <div :class="{ 'has-error': errors[0] }">
            <textarea
              v-if="!isBillRate && showPayRateNote"
              id="pay-rate-note"
              v-model="payRateNote"
              placeholder="Add a note to pay rate change"
              rows="4"
              class="form-control text"
            />
            <textarea
              v-if="isBillRate && showBillRateNote"
              id="bill-rate-note"
              v-model="billRateNote"
              placeholder="Add a note to bill rate change"
              rows="4"
              class="form-control text"
            />
            <div
              style="display: flex; align-items: center; min-height: 20px;"
              :class="{ 'help-block': errors[0] }"
            >
              <span
                v-if="errors[0]"
              >
                {{ errors[0] }}
              </span>
              <span
                class="note-counter"
                :class="{ 'help-block': errors[0] }"
              >
                {{
                  (isBillRate ? billRateNote?.length || 0 : payRateNote?.length || 0)
                }}/100
              </span>
            </div>
          </div>
        </div>
      </ValidationProvider>

      <MembersRatesActivity
        v-if="selectedTab"
        :title="selectedTab.text"
        wrapper-classes="mt-20"
      />
      <slot name="actions" />
      <AppDialog
        v-if="canEditTimesheets && showAdjustTimesheetsDialog"
        show
        data-testid="adjust-timesheets-dialog"
        title="Edit past timesheets"
        size="large"
        append-to-body
        @close="toggleAdjustTimesheetsDialog(false)"
      >
        <AdjustTimesheetsForm
          v-bind="adjustTimesheetsFormProps"
          @close="toggleAdjustTimesheetsDialog(false)"
        />
      </AppDialog>
    </div>
  </div>
</template>

<script>
import { extend } from 'vee-validate';
import { mapActions, mapGetters, mapState } from 'vuex';
import { MEMBERSHIP_RATE_TYPE_OPTIONS, PAYMENT_RATES, RATE_LIMIT, RATE_TYPE_EXTERNAL } from '~/app_constants';
import MembersRatesActivity from '~/components/members/MembersRatesActivity.vue';
import PayPeriodCustomDatesSelector from '~/components/shared/PayPeriodCustomDatesSelector.vue';
import { mapFormFields } from '~/utils/form_utils';
import AdjustTimesheetsForm from './AdjustTimesheetsForm.vue';

extend('payRateRule', {
  validate: (value) => {
    if (parseFloat(value) >= 0.01) {
      return true;
    }

    return `Pay rate must be greater than or equal to 0.01.
        If you are trying to skip this member in Payroll set them to Manual instead.`;
  },
});

extend('noteMax', {
  params: ['length'],
  validate(value, { length }) {
    return !value || value.length <= length;
  },
  message: `Note must be {length} characters or less`,
});

export default {
  name: 'MemberDialogTabPayments',
  components: { MembersRatesActivity, AdjustTimesheetsForm, PayPeriodCustomDatesSelector },
  data: () => {
    return {
      RATE_TYPE_EXTERNAL,
      RATE_LIMIT,
      PAYMENT_RATES,
      defaultSelect2Options: {
        allowClear: false,
        multiple: false,
        width: '100%',
      },
      billRateEffectiveDate: {},
      payRateEffectiveDate: {},
      timesheetsEnabledTooltip: 'Tooltip Text',
      effectiveDateTooltip: 'Tooltip Text',
      toggleButtonTooltipLabel: 'Payment Rates',
      toggleButtonTooltipText:
        '<p><b>Pay rate</b> is the one you pay to this member directly.</p><p><b>Bill rate</b> is to charge your customers for this member.</p>',
      toggleButtonTooltipOptions: { html: true },
      tooltipOptions: Object.freeze({
        placement: 'top',
      }),
      showEffectiveDateConfirm: false,
      rateEffectiveDateConfirmType: 'pay_rate',
      initialBillRate: '',
      initialPayRate: '',
      initialPayPeriod: '',
      previousPayPeriod: null,
      initialPeriodWeekDay: null,
      initialPeriodStartDay: null,
      initialPeriodSecondStartDay: null,
      initialPeriodAnchorDate: null,
      initialTwicePerMonthHasSelection: false,
      initialNote: '',
      initialTimesheetsEnabled: false,
      showAdjustTimesheetsDialog: false,
      showBillRateNote: false,
      showPayRateNote: false,
    };
  },
  computed: {
    ...mapState(['membership', 'organization', 'errors']),
    ...mapGetters([
      'biweeklyPeriod',
      'canApproveTimesheets',
      'hasFixedRate',
      'payPeriodIsNone',
      'payPeriodIsExternal',
      'payPeriodOptions',
      'hasPayPeriod',
      'rateTypeOptions',
      'paymentSection',
      'periodStartDates',
      'organizationTimeZone',
      'availableRateEffectiveDates',
      'defaultRateEffectiveDate',
      'hasCustomPayPeriodDates',
    ]),
    ...mapFormFields('membership', {
      billRate: 'payments.bill_rate',
      fixedAmount: 'payments.fixed_amount',
      periodStart: 'payments.period_start',
      payPeriod: 'payments.pay_period',
      payRate: 'payments.pay_rate',
      rateEffectiveDate: 'payments.rate_effective_date',
      rateType: 'payments.rate_type',
      timesheetsEnabled: 'payments.timesheets_enabled',
      payRateStartDate: 'payments.pay_rate_start_date',
      billRateStartDate: 'payments.bill_rate_start_date',
      payRateNote: 'payments.pay_rate_note',
      billRateNote: 'payments.bill_rate_note',
      periodWeekDay: 'payments.period_week_day',
      periodStartDay: 'payments.period_start_day',
      periodSecondStartDay: 'payments.period_second_start_day',
      periodAnchorDate: 'payments.period_anchor_date',
    }),
    timesheetToggleDisabled() {
      let disabled = !this.hasPayPeriod;

      if (this.payPeriodIsNone) {
        disabled = true;
      } else if (!this.payPeriodIsExternal) {
        disabled = false;
      }

      return disabled;
    },
    minRateEffectiveDate() {
      return {
        payRate: this.availableRateEffectiveDates.pay_rate[0] && moment(this.availableRateEffectiveDates.pay_rate[0]),
        billRate:
          this.availableRateEffectiveDates.bill_rate[0] && moment(this.availableRateEffectiveDates.bill_rate[0]),
      };
    },
    maxRateEffectiveDate() {
      return moment().tz(this.organizationTimeZone?.for);
    },
    showPayRateEffectiveDate() {
      return this.rateType !== MEMBERSHIP_RATE_TYPE_OPTIONS.find((option) => option.key === 'fixed_amount').id;
    },
    organizationCurrency() {
      return this.organization.currency.currency;
    },
    isBillRate() {
      return !!this.paymentSection;
    },
    selectedTab() {
      return this.PAYMENT_RATES.find((type) => type.value === this.paymentSection);
    },
    showPeriodStartOptions() {
      return !this.isBillRate && !this.payPeriodIsNone && !this.payPeriodIsExternal;
    },
    customPayPeriodOptions() {
      return this.payPeriodOptions.map((option) => ({ id: option.id, text: option.text }));
    },
    customPayPeriodSelectorValue() {
      return {
        payPeriodId: this.payPeriod,
        previousPayPeriodId: this.previousPayPeriod,
        periodWeekDay: this.periodWeekDay,
        periodStartDay: this.periodStartDay,
        periodSecondStartDay: this.periodSecondStartDay,
        periodAnchorDate: this.periodAnchorDate,
      };
    },
    customPayPeriodSelectorInitialState() {
      return {
        initial_pay_period: this.initialPayPeriod,
        initial_period_week_day: this.initialPeriodWeekDay,
        initial_period_start_day: this.initialPeriodStartDay,
        initial_period_second_start_day: this.initialPeriodSecondStartDay,
        initial_period_anchor_date: this.initialPeriodAnchorDate,
        initial_twice_per_month_has_selection: this.initialTwicePerMonthHasSelection,
      };
    },
    startDates() {
      const period = this.payPeriodOptions.find((period) => period.id === this.payPeriod);

      return (
        period?.key &&
        this.periodStartDates &&
        this.periodStartDates[period.key]?.map((date) => ({
          value: date,
          text: this.formatPeriodDate(date),
        }))
      );
    },
    canEditTimesheets() {
      return this.membership.timesheets_info?.can_edit;
    },
    adjustTimesheetsFormProps() {
      const minDateFrom = this.membership.timesheets_info?.allow_adjust_since;
      const payPeriodKey = this.membership.payments.pay_period;

      return {
        organizationId: this.organization?.id,
        userId: this.membership.user?.id,
        minDateFrom: minDateFrom ? moment(minDateFrom) : moment().subtract(3, 'months'),
        maxDateTo: moment().date() < 15 ? moment().endOf('month') : moment().add(1, 'month').date(15).startOf('day'),
        allowAdjustTimesheets: true,
        initialPayPeriod: this.payPeriodOptions.find((period) => period.id === payPeriodKey)?.key || 'weekly',
        initialDateFrom: moment(this.membership.timesheets_info?.default_adjust_since)
      };
    },
  },
  created() {
    this.setDefaultEffectiveDates();
    // Not the best solution, but all the data is in the store, and we need to reset it here
    this.setInitialValues();
  },
  methods: {
    ...mapActions(['updateField', 'updatePaymentSection']),
    onPayRateEffectiveDateInput(date) {
      const dateWithTZOffset = moment.tz(date, this.organizationTimeZone?.for);

      this.payRateStartDate = date.format();
      this.compareEffectiveDates(dateWithTZOffset, 'pay_rate');
    },
    onBillRateEffectiveDateInput(date) {
      const dateWithTZOffset = moment.tz(date, this.organizationTimeZone?.for);

      this.billRateStartDate = date.format();
      this.compareEffectiveDates(dateWithTZOffset, 'bill_rate');
    },
    onEffectiveDateConfirmCancel() {
      this.setDefaultEffectiveDates();
      this.resetFields();
      this.showEffectiveDateConfirm = false;
    },
    onEffectiveDateConfirmConfirm() {
      this.showEffectiveDateConfirm = false;
    },
    compareEffectiveDates(date, key) {
      if (date.isBefore(this.defaultRateEffectiveDate[key]) || date.isSame(this.defaultRateEffectiveDate[key])) {
        this.rateEffectiveDateConfirmType = key;
        this.showEffectiveDateConfirm = true;
      }
    },
    onPayPeriodSelect() {
      if (this.canApproveTimesheets && this.payPeriodIsNone) {
        this.timesheetsEnabled = false;
      }
      this.periodStart = this.startDates?.[0]?.value;
    },
    onCustomPayPeriodSelectorInput(nextState) {
      this.payPeriod = nextState.payPeriodId;
      this.previousPayPeriod = nextState.previousPayPeriodId;
      this.periodWeekDay = nextState.periodWeekDay;
      this.periodStartDay = nextState.periodStartDay;
      this.periodSecondStartDay = nextState.periodSecondStartDay;
      this.periodAnchorDate = nextState.periodAnchorDate;

      if (this.canApproveTimesheets && this.payPeriodIsNone) {
        this.timesheetsEnabled = false;
      }
    },
    setDefaultEffectiveDates() {
      // 'Mon, Mar 6, 2023'
      this.payRateEffectiveDate = moment().tz(this.organizationTimeZone?.for);
      this.billRateEffectiveDate = moment().tz(this.organizationTimeZone?.for);
    },
    formatPeriodDate(date) {
      return moment(date).format('ddd, D MMM, YYYY');
    },
    togglePaymentSection(value) {
      this.$refs.payment_rates.setFlags({ dirty: true });

      this.updatePaymentSection(value);

      window.analytics?.track('Member rate toggle clicked', { clicked: value ? 'Bill rate' : 'Pay rate' });
    },
    formErrors(field) {
      return this.errors && this.errors[field];
    },
    setInitialValues() {
      this.initialBillRate = this.billRate;
      this.initialPayRate = this.payRate;
      this.initialPayPeriod = this.payPeriod ?? '';
      this.previousPayPeriod = this.payPeriod ?? '';
      this.initialPeriodWeekDay = this.periodWeekDay ?? null;
      this.initialPeriodStartDay = this.periodStartDay ?? null;
      this.initialPeriodSecondStartDay = this.periodSecondStartDay ?? null;
      this.initialPeriodAnchorDate = this.periodAnchorDate ?? null;
      this.initialTwicePerMonthHasSelection =
        (this.payPeriod === '1') &&
        this.periodStartDay != null &&
        this.periodSecondStartDay != null;
      this.initialTimesheetsEnabled = this.timesheetsEnabled;
      this.initialNote = '';
    },
    resetFields() {
      this.payRate = this.initialPayRate;
      this.billRate = this.initialBillRate;
      this.payPeriod = this.initialPayPeriod;
      this.timesheetsEnabled = this.initialTimesheetsEnabled;
      this.billRateNote = this.initialNote;
      this.payRateNote = this.initialNote;
    },
    effectiveDateConfirmMessage() {
      const date = moment(
        this.rateEffectiveDateConfirmType === 'pay_rate' ? this.payRateEffectiveDate : this.billRateEffectiveDate,
      ).format('MMM D, YYYY');

      return `The effective date of this rate change is set prior to existing rate changes and will override anything set after ${date}.
         You will need to save the page to confirm these changes.`;
    },
    toggleAdjustTimesheetsDialog(flag) {
      this.showAdjustTimesheetsDialog = flag;
    },
    removePayRateNote() {
      this.payRateNote = '';
      this.updateField('payments.pay_rate_note', '');
      this.showPayRateNote = false;
      this.$refs.noteProvider?.reset(); // Reset validation for pay rate note
    },
    displayPayRateNote() {
      this.showPayRateNote = true;
    },
    removeBillRateNote() {
      this.billRateNote = '';
      this.updateField('payments.bill_rate_note', '');
      this.showBillRateNote = false;
      this.$refs.noteProvider?.reset(); // Reset validation for bill rate note
    },
    displayBillRateNote() {
      this.showBillRateNote = true;
    },
  },
};
</script>
